import { TaxInvoiceStatusResponse } from '@revenuemarket/utils/constants/taxInvoice';
import QueryString from 'qs';

import { API } from '~apis/axios/instance';
import { TAX_INVOICE_STATUS } from '~components/TaxInvoice/TaxInvoiceIssueTable';
import { IssuanceType, PurposeType, TaxationType } from '~components/TaxInvoice/type';
import {
  TaxInvoiceRequest,
  TaxInvoice,
  TaxInvoiceResponse,
  TaxInvoiceType,
  EmailResendItem,
} from '~models/tax-invoice';
import {
  CursorQueryRequestParams,
  CursorQueryResponse,
  Page,
  ResponseBaseWithData,
} from '~types/response';

export type TAX_INVOICE_TYPE = 'SALES' | 'PURCHASE' | 'CONSIGNMENT';
export interface IDepositInfoItem {
  depositDate: string;
  depositAmount: number;
}

export interface IItemListItem {
  name: string;
  unitPrice: number;
  quantity?: number;
  totalAmount?: number;
  taxAmount?: number;
  supplyAmount?: number;
  calculateType?: 'UNIT_PRICE' | 'TOTAL_PRICE';
  flag?: number;
  unit: string;
  date?: string;
  basePriceFromPrefill?: number;
}

export interface GetTaxInvoiceStatusResponse extends ResponseBaseWithData {
  data: TaxInvoiceStatusResponse;
}

export const getTaxInvoices = ({
  companyId,
  startDate,
  endDate,
  type,
  searchParam,
  page,
  size,
  sort,
  businessEntityId,
  taxationType,
  modificationType,
}: TaxInvoiceRequest) => {
  const query = QueryString.stringify(
    {
      startDate,
      endDate,
      type,
      searchParam,
      page,
      size,
      sort,
      businessEntityId,
      taxationType,
      modificationType,
    },
    {
      arrayFormat: 'repeat',
      skipNulls: true,
      filter: (prefix, value) => (value === '' ? undefined : value),
    },
  );

  return API.get<ResponseBaseWithData<TaxInvoiceResponse>>(
    `/api/v2/companies/${companyId}/tax-invoices?${query}`,
  );
};

export const getTaxInvoice = ({
  companyId,
  type,
  id,
}: {
  companyId: string;
  type: TaxInvoiceType;
  id: string;
}) => {
  return API.get<ResponseBaseWithData<TaxInvoice>>(
    `/api/v2/companies/${companyId}/tax-invoices/${id}`,
    {
      params: {
        type,
      },
    },
  );
};

type ConnectStatus =
  // 발급화면으로 이동
  | 'CONNECTED'
  //회원사로 추가 요청 필요 → 회원사로 추가 요청  API
  | 'NEED_CONNECT'
  // 인증서 등록 → 인증서 URL
  | 'NEED_CERTIFICATION'
  // 바로빌 가입 필요 → 바로빌 가입 API
  | 'NEED_ADD_MEMBER_CORP'
  // 회원사로 추가 요청을 했고 대기상태 → 팝업
  | 'WAITING_ADD_MEMBER_CORP';

interface TaxInvoiceConnectCheckResponse {
  companyId: string;
  connectStatus: ConnectStatus;
  additionalUrl: string;
}
/**
 * 바로빌 연동상태를 체크 하는 API : 발급 화면 진입시 매번 호출
 * @param param0
 * @returns
 */
export const getIssueConnectStatus = ({ companyId }: { companyId: string }) => {
  return API.get<ResponseBaseWithData<TaxInvoiceConnectCheckResponse>>(
    `/api/v2/companies/${companyId}/invoice-issuance/connecting/status`,
  );
};

/**
 * 기존 바로빌 회원을, 클로브의 회원사로 추가 요청
 * @param param0
 * @returns
 */
export const addClobeMember = ({
  companyId,
  memberId,
}: {
  companyId: string;
  memberId: string;
}) => {
  return API.post<ResponseBaseWithData<TaxInvoiceConnectCheckResponse>>(
    `/api/v2/companies/${companyId}/invoice-issuance/connecting/member-corp`,
    {
      data: {
        memberId,
      },
    },
  );
};

/**
 * 바로빌로 가입 및 회원사로 추가
 * @returns
 */
export const signupBarobill = ({
  companyId,
  request,
}: {
  companyId: string;
  request: {
    memberName: string;
    memberId: string;
    memberPw: string;
    phoneNumber: string;
    email: string;
  };
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/invoice-issuance/connecting`,
    {
      data: request,
    },
  );
};

export type ModifyReason =
  | 'CORRECTING_MISSTATEMENTS'
  | 'CHANGE_IN_SUPPLY_PRICE'
  | 'RETURN_OF_GOODS'
  | 'CONTRACT_CANCELLED'
  | 'POST_OPENING_LOCAL_LC'
  | 'DOUBLE_ISSUANCE';

export interface InvoiceModification {
  id?: number;
  reason: ModifyReason;
  originalApprovalNo: string;
  issueDate: string;
  isDraft: boolean;
  isInstant: boolean;
  correctingMisstatements?: ICorrectingMisstatements;
  changeInSupplyPrice?: IChangeInSupplyPrice;
  returnOfGoods?: IReturnOfGoods;
  contractCancelled?: IContractCancelled;
  postOpeningLocalLc?: IPostOpeningLocalLc;
  emailResends: EmailResendItem[];
}
export interface ICorrectingMisstatements {
  supplierInfo: IStoreInfo;
  contractorInfo: IStoreInfo;
  purposeType: PurposeType;
  taxationType: TaxationType;
  issuanceType: IssuanceType;
  itemList: IItemListItem[];
  isManualInput: boolean;
  businessEntityInfo: IBusinessEntityInfo;
}

interface IMembersItem {
  name: string;
  email: string;
}
export interface IStoreInfo {
  businessRegNo: string;
  taxIdNumber: string;
  corpName: string;
  corpCeo: string[];
  corpAddress: string;
  corpAddressDetail?: string;
  businessClass: string;
  businessType: string;
  members: IMembersItem[];
}

export interface IBusinessEntityInfo {
  businessEntityId: number;
  businessMemberIdList?: number[];
}
interface IChangeInSupplyPrice {
  changeDate: string;
  itemList: IItemListItem[];
}
interface IReturnOfGoods {
  changeDate: string;
  itemList: IItemListItem[];
}
interface IContractCancelled {
  cancelDate: string;
}
interface IPostOpeningLocalLc {
  localLcOpeningDate: string;
}

export interface InvoiceIssuance {
  id?: number;
  writtenDate: string;
  issueDate: string;
  depositInfo: IDepositInfoItem[];
  supplierInfo: IStoreInfo;
  contractorInfo: IStoreInfo;
  purposeType: PurposeType;
  taxationType: TaxationType;
  issuanceType: IssuanceType;
  itemList: IItemListItem[];
  isInstant: boolean;
  isDraft: boolean;
  isManualInput: boolean;
  businessEntityInfo: IBusinessEntityInfo;
  modificationReason?: ModifyReason;
  originApprovalNo?: string;
  approvalNo?: string;
  emailResends: EmailResendItem[];
  remark?: string;
  note?: string;
}

export type InvoiceModificationIssuance = InvoiceIssuance & InvoiceModification;

export const getInvoiceIssuanceNewModificationInfo = ({
  companyId,
  invoiceIssuanceId,
}: {
  companyId: string;
  invoiceIssuanceId: number;
}) => {
  return API.get<ResponseBaseWithData<InvoiceIssuance>>(
    `/api/v2/companies/${companyId}/invoice-issuance/${invoiceIssuanceId}`,
  );
};

export const getInvoiceIssuanceExistModificationInfo = ({
  companyId,
  invoiceIssuanceId,
}: {
  companyId: string;
  invoiceIssuanceId: number;
}) => {
  return API.get<ResponseBaseWithData<InvoiceModification>>(
    `/api/v2/companies/${companyId}/invoice-issuance/modification/${invoiceIssuanceId}`,
  );
};

export const getIssuanceByApprovalNo = ({
  companyId,
  approvalNo,
}: {
  companyId: string;
  approvalNo: string;
}) => {
  return API.get<ResponseBaseWithData<InvoiceIssuance>>(
    `/api/v2/companies/${companyId}/invoice-issuance/approvals/${approvalNo}`,
  );
};

export interface InvoiceIssuanceRequest extends Omit<InvoiceIssuance, 'emailResends'> {
  emailRecipientList: { email: string }[] | [];
}

export const issueTaxInvoice = ({
  companyId,
  request,
}: {
  companyId: string;
  request: InvoiceIssuanceRequest;
}) => {
  return API.post<ResponseBaseWithData>(`/api/v2/companies/${companyId}/invoice-issuance`, {
    data: request,
  });
};

export interface InvoiceModificationRequest extends Omit<InvoiceModification, 'emailResends'> {
  emailRecipientList: { email: string }[] | [];
}

export const modifyTaxInvoice = ({
  companyId,
  request,
}: {
  companyId: string;
  request: InvoiceModificationRequest;
}) => {
  return API.post<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/invoice-issuance/modification`,
    {
      data: request,
    },
  );
};

export const syncInvoiceDetail = ({
  companyId,
  approvalNo,
  type = 'SALES',
}: {
  companyId: string;
  approvalNo: string;
  type?: TaxInvoiceType;
}) => {
  return API.put<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/tax-invoices/${approvalNo}/detail?type=${type}`,
  );
};

export const cancelIssuance = ({
  companyId,
  invoiceIssuanceId,
}: {
  companyId: string;
  invoiceIssuanceId: number;
}) => {
  return API.patch<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/invoice-issuance/${invoiceIssuanceId}/cancel`,
  );
};

export type InvoiceIssuanceResult = InvoiceIssuance & {
  isModification: boolean;
  status: TAX_INVOICE_STATUS;
  originalApprovalNo?: string;
  id: number;
  failDescription?: string;
  writtenBy?: string;
};

export interface InvoiceIssuanceListRequest {
  page: number;
  size: number;
  sort: string;
  status: TAX_INVOICE_STATUS;
  start: string;
  isModification: boolean;
  end: string;
  taxationType: TaxationType[];
  keyword: string;
  writerId: string;
}

export const getInvoiceIssuances = ({
  companyId,
  request,
}: {
  companyId: string;
  request?: Partial<InvoiceIssuanceListRequest>;
}) => {
  const query = QueryString.stringify(request, {
    arrayFormat: 'repeat',
    skipNulls: true,
    filter: (prefix, value) => (value === '' ? undefined : value),
  });

  return API.get<ResponseBaseWithData<Page<InvoiceIssuanceResult>>>(
    `/api/v2/companies/${companyId}/invoice-issuance?${query}`,
  );
};

export const uploadExcelFiles = (companyId: string, file: File) => {
  const formData = new FormData();
  formData.append('excel', file);
  return API.post(`/api/v2/companies/${companyId}/invoice-issuance/excel`, {
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateDepositInfo = ({
  companyId,
  approvalNo,
  type,
  request,
}: {
  companyId: string;
  approvalNo: string;
  type: TAX_INVOICE_TYPE;
  request: IDepositInfoItem;
}) => {
  return API.put(
    `/api/v2/companies/${companyId}/tax-invoices/${approvalNo}/deposit-info?type=${type}`,
    {
      data: [request],
    },
  );
};

export interface InvoiceIssuanceCountRequest {
  companyId: string;
  start: string;
  end: string;
}

export const getInvoiceIssuanceCount = ({ companyId, start, end }: InvoiceIssuanceCountRequest) => {
  return API.get<ResponseBaseWithData<Record<TAX_INVOICE_STATUS, number>>>(
    `/api/v2/companies/${companyId}/invoice-issuance/status-count?start=${start}&end=${end}`,
  );
};

export interface Validate {
  totalCount: number;
  successCount: number;
  failCount: number;
  errorMessage: string[];
  successMessage: string[];
  isSuccess: boolean;
}

export const validateExcelFiles = (companyId: string, file: File) => {
  const formData = new FormData();
  formData.append('excel', file);
  return API.post<ResponseBaseWithData<Validate>>(
    `/api/v2/companies/${companyId}/invoice-issuance/excel/validate`,
    {
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
};

export interface EmailResendRequest {
  companyId: string;
  invoiceIssuanceId: number;
  resendEmails: string[];
}

export const resendEmail = ({ companyId, invoiceIssuanceId, resendEmails }: EmailResendRequest) => {
  const resendEmailList = resendEmails.map((email) => ({ email }));
  return API.post<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/invoice-issuance/${invoiceIssuanceId}/resend`,
    {
      data: resendEmailList,
    },
  );
};

export interface RetryEmailSendRequest {
  companyId: string;
  invoiceIssuanceId: number;
  resendId: number;
}

export const retryEmailSend = ({
  companyId,
  invoiceIssuanceId,
  resendId,
}: RetryEmailSendRequest) => {
  return API.post<ResponseBaseWithData>(
    `/api/v2/companies/${companyId}/invoice-issuance/${invoiceIssuanceId}/resend/${resendId}/retry`,
  );
};

export interface TaxInvoiceQueryParams extends CursorQueryRequestParams {
  type?: TaxInvoiceType;
  searchParam?: string;
  businessEntityId?: string;
  taxationType?: TaxationType[];
  modificationType?: string;
  cursorReportingDate?: string;
}

export const getTaxInvoicesByCursor = ({
  companyId,
  queryParams,
}: {
  companyId: string;
  queryParams: TaxInvoiceQueryParams;
}) => {
  const queryString = QueryString.stringify(queryParams, {
    arrayFormat: 'repeat',
  });
  return API.get<
    ResponseBaseWithData<
      CursorQueryResponse<TaxInvoice> & {
        nextCursor: { reportingDate: string; id: number } | null;
      }
    >
  >(`/api/v2/companies/${companyId}/tax-invoices/by-cursor?${queryString}`);
};

export const createTaxInvoiceMemo = ({
  companyId,
  invoiceId,
  memo,
  taxInvoiceType,
}: {
  companyId: string;
  invoiceId: string;
  memo: string;
  taxInvoiceType: TaxInvoiceType;
}) => {
  return API.post(`/api/v3/companies/${companyId}/tax-invoices/${invoiceId}/memos`, {
    data: {
      content: memo,
      taxInvoiceType: taxInvoiceType,
    },
  });
};
